import FJTextAnimation from './FJTextAnimation';
import FJAnimationTimelineData from './FJAnimationTimelineData';

const STATE = {
    IDLE: 'IDLE',
    ACTIVE: 'ACTIVE',
};

class FJTextAnimationPlayer {
    _duration = 0; // 播放的时长，包含进出场动画
    _startTime = 0; // 动画开始播放的时间
    _isLoop = false;
    _textTargets = []; // 做动画的文字

    // 是否整体动画
    _textGetAnimationByTimeFun = null;
    // 获取多个复制出来的动画目标
    _getAnimationMultipleTargets = null;

    constructor(props = {}) {
        this._textGetAnimationByTimeFun = props.textGetAnimationByTimeFun;
        this._getAnimationMultipleTargets = props.getAnimationMultipleTargets;
        this._state = STATE.IDLE;
    }

    activate() {
        this._state = STATE.ACTIVE;
    }

    freeze() {
        this._state = STATE.IDLE;
    }

    isActive() {
        return this._state === STATE.ACTIVE;
    }

    /**
     * @desc 创建文字动画
     * @param targets
     * @param animationInName - 进场动画
     * @param animationOutName - 退场动画
     * @param {{}} timeData - 时间数据，单位ms
     * @param timeData.startTime - 动画开始时间
     * @param timeData.showDuration - 进场动画持续时间
     * @param timeData.hideDuration - 退场动画持续时间
     * @param timeData.staticDuration - 无动画持续时间
     * @param options
     * @param options.speedIn - 进场速度
     * @param options.speedOut - 退场速度
     * @param options.animationTargetContainer - 动画元素的父组件
     * @param options.animationTarget - 动画元素组件
     */
    createAnimation(targets, animationInName, animationLoopName, animationOutName, timeData, options = {}) {
        if (!targets) return;
        this._textTargets = targets;
        let hasInAnimation = FJTextAnimation.isEffectiveAnimation(animationInName);
        let hasLoopAnimation = FJTextAnimation.isEffectiveAnimation(animationLoopName);
        let hasOutAnimation = FJTextAnimation.isEffectiveAnimation(animationOutName);
        if (!hasInAnimation && !hasOutAnimation && !hasLoopAnimation) return;
        this._isLoop = FJTextAnimation.isLoopAnimation(animationLoopName);
        // 时间单位ms
        const { delay, endDelay, duration, showDuration, hideDuration, staticDuration, loopDuration } = timeData;

        const {
            speedIn = 1,
            speedLoop = 1,
            speedOut = 1,
            isTextEditMode,
            textColor,
            animationTargetContainer,
            animationTarget,
            animationTextAllTarget,
            animationSupportTarget,
            textAllAnimationActive,

            animationTextColor, // 字幕动画才会用到
            animationBackgroundColor, // 字幕动画才会用到
            isSubtitleAnimation, // 字幕动画才会用到
            wordTimestampList, // 字幕动画才会用到
        } = options;

        const elementDuration = duration - delay - endDelay;
        const timeLineTimeData = new FJAnimationTimelineData({
            startTime: delay,
            endTime: delay + elementDuration,
        });

        // TODO 一些特殊业务或者兼容性处理或其他的一起关键数据，在重构时发现这些不应该在FJTextAnimationPlayer内部来组合，由于这里缺少描述和回溯来源很麻烦，所以先临时放在这
        const textAnimationOptions = {
            textColor: textColor,
            isSubtitleAnimation: isSubtitleAnimation,
            wordTimestampList: wordTimestampList,
            animationTextColor: animationTextColor,
            animationBackgroundColor: animationBackgroundColor,
        };

        if (hasInAnimation && showDuration > 0) {
            const endTime = delay + showDuration;
            timeLineTimeData.addTimeData({
                startTime: delay,
                endTime: endTime,
                animationName: animationInName,
                duration: showDuration,
                options: textAnimationOptions,
            });
        }

        if (hasLoopAnimation && loopDuration > 0) {
            timeLineTimeData.addTimeData({
                startTime: delay + showDuration,
                endTime: delay + showDuration + loopDuration,
                animationName: animationLoopName,
                duration: loopDuration,
                options: textAnimationOptions,
            });
        }

        if (hasOutAnimation && hideDuration > 0) {
            timeLineTimeData.addTimeData({
                startTime: delay + showDuration + loopDuration + staticDuration,
                endTime: delay + showDuration + loopDuration + staticDuration + hideDuration,
                animationName: animationOutName,
                duration: hideDuration,
                options: textAnimationOptions,
            });
        }

        const { animationTimes } = timeLineTimeData;
        if (animationTimes.length) {
            this._startTime = animationTimes[0].startTime;
            const lastAnimationTimeData = animationTimes[animationTimes.length - 1];
            this._duration = lastAnimationTimeData.endTime;
        }

        this._animation = new FJTextAnimation({
            animationContainer: animationTargetContainer,
            animationTargets: targets,
            animationTextAllTarget: animationTextAllTarget,
            animationSupportTarget: animationSupportTarget,
            textAllAnimationActive: textAllAnimationActive,
            animationInName: animationInName,
            animationLoopName: animationLoopName,
            animationOutName: animationOutName,
            timeLineTimeData: timeLineTimeData,
            textGetAnimationByTimeFun: this._textGetAnimationByTimeFun,
            animationMultipleTargets: this._getAnimationMultipleTargets
                ? this._getAnimationMultipleTargets.bind(this)
                : null,
            options: {
                animationComposition: animationTarget,
            },
        });

        animationTimes.forEach(data => {
            const { animationName, duration, startTime, endTime } = data;
            const speed =
                animationName === animationInName
                    ? speedIn
                    : animationName === animationLoopName
                      ? speedLoop
                      : speedOut;
            this._animation.addAnimation(targets, animationName, duration, 0, startTime, endTime, {
                ...textAnimationOptions,
                speed: speed,
                isTextEditMode: isTextEditMode,
            });
        });
    }

    reset() {
        if (this._animation) {
            this._animation.reset();
        }
    }

    prepareStartAnimation() {
        if (this._animation) {
            this._animation.prepareStartAnimation();
        }
    }

    /**
     * @desc 播放
     * @param currentTime - 动画开始播放的时间点,单位ms
     */
    play(currentTime) {
        if (this._animation && currentTime < this._duration) {
            // if (this._isLoop && typeof currentTime !== 'undefined' && currentTime < this._startTime) {
            //     let sTime;
            //     let __play = time => {
            //         if (!sTime) sTime = time;
            //         if (currentTime + (time - sTime) >= this._startTime) {
            //             this._animation.play();
            //         } else {
            //             this._playRequestTimer = window.requestAnimationFrame(__play);
            //         }
            //     };
            //     this._playRequestTimer = window.requestAnimationFrame(__play);
            // } else {
            this._animation.play();
            // }
        }
    }

    stop() {
        if (this._animation) {
            this.pause();
            this.reset();
        }
    }

    pause() {
        if (this._playRequestTimer) {
            window.cancelAnimationFrame(this._playRequestTimer);
        }
        if (this._animation && !this._animation.isPaused()) this._animation.pause();
    }

    isPaused() {
        if (this._animation) {
            return this._animation.isPaused();
        }
        return false;
    }

    /**
     * []@desc 跳转到某一时间点
     * @param {number} time - 时间，单位为毫秒
     */
    seek(time) {
        if (!this._animation) return;
        this.pause();
        // time大于需要播放的时长了则time等于结束播放的时间点
        // if (time >= this._duration) time = this._duration;
        // if (this._isLoop) {
        //     // 还未开始播放则seek到0
        //     if (time <= this._startTime) {
        //         this._animation.seek(0);
        //     } else {
        //         let duration = this._animation.getDuration();
        //         let t = time - this._startTime; // 相对于动画实际开始播放的时间
        //         let t1 = t >= 0 ? t % duration : time;
        //         this._animation.seek(t1);
        //     }
        // } else
        this._animation.seek(time);
    }

    /**
     * 由于精度问题，coverFrameSeek需要使用Math.floor取整，仅用于goToCoverFrame
     * @param {*} time 毫秒
     */
    coverFrameSeek(time) {
        // time = Math.floor(time * 100) / 100;
        this._animation && this._animation.seek(time);
    }

    /**
     * @desc 销毁动画
     */
    destroyAnimation() {
        if (this._animation) {
            this._animation.destroy(this._textTargets);
            this._animation = null;
            this._textTargets = [];
        }
    }

    updateContainerOverflow(aniName, targets = []) {
        if (this._animation) this._animation.updateContainerOverflow(aniName, targets);
    }

    get duration() {
        return this._duration;
    }

    get startTime() {
        return this._startTime;
    }
}

export default FJTextAnimationPlayer;
